import { GetPartnerBusinessProfileResponse } from '@/api/type/types';
import { MultiLineText } from '@/components/text/MultiLineText';
import { useAppSelector } from '@/stores/hooks';
import {
  BusinessDaysType,
  businessProfile,
} from '@/stores/slices/business-profile/businessProfileSlice';
import { fonts } from '@/theme/fonts';
import { formatBusinessHour } from '@/utils/date-time/formatBusinessHour';
import { getCountryNameByCode } from '@/utils/name/getCountryName';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';

interface BusinessTimeSettingsProps {
  profile: GetPartnerBusinessProfileResponse;
  isEdit: boolean;
}

/** 미입력시 기본 시간, 서버는 09:00 ~ 18:00 */
const DEFAULT_OPEN_TIME = '00:00';
const DEFAULT_CLOSE_TIME = '23:59';

const BusinessTimeSettings = ({
  profile,
  isEdit,
}: BusinessTimeSettingsProps) => {
  const [openTime, setOpenTime] = useState<string>(
    profile.businessTime.openTime.slice(0, 5)
  );
  const [closeTime, setCloseTime] = useState<string>(
    profile.businessTime.closeTime.slice(0, 5)
  );

  console.log('?openTime', openTime);
  const openRef = useRef<HTMLInputElement>(null);
  const closeRef = useRef<HTMLInputElement>(null);
  const dispatch = useDispatch();
  const { putBusinessProfile } = useAppSelector(
    (state) => state.businessProfile
  );
  const { t } = useTranslation();
  const movedRef = useRef(false);

  const dayTitles = [
    t('sun'),
    t('mon'),
    t('tue'),
    t('wed'),
    t('thu'),
    t('fri'),
    t('sat'),
  ];

  const businessDaysWithTitle = profile.businessDays.map((item, idx) => ({
    ...item,
    title: dayTitles[idx],
  }));

  const onClickDays = useCallback(
    (day: BusinessDaysType) => () => {
      if (!putBusinessProfile) return;

      const updatedBusinessDays = putBusinessProfile.businessDays?.map((d) =>
        d.dayOfWeek === day.dayOfWeek
          ? { ...d, active: !d.active } // 선택한 요일만 토글
          : d
      );

      dispatch(
        businessProfile.putBusinessProfile({
          businessDays: updatedBusinessDays,
        })
      );
    },
    [dispatch, putBusinessProfile]
  );

  console.log('putBusinessProfile', putBusinessProfile);

  /** 시간 검증용 */
  const isValidTime = (value: string) => {
    const match = value.match(/^(\d{2}):(\d{2})$/);
    if (!match) return false;

    const hour = Number(match[1]);
    const minute = Number(match[2]);

    return hour >= 0 && hour <= 23 && minute >= 0 && minute <= 59;
  };

  /** 24시 -> 00시 보정 */
  const normalizeBusinessTime = (value: string) => {
    if (!value) return '00:00';

    const match = value.match(/^(\d{1,2}):?(\d{0,2})$/);
    if (!match) return '00:00';

    let hour = Number(match[1]);
    let minute = Number(match[2] || 0);

    if (hour >= 24) hour = 0;
    if (minute >= 60) minute = 0;

    return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
  };

  const onChangeOpenTime = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;

    if (!/^[0-9:]*$/.test(raw)) return;

    const formatted = formatTimeWithAutoColon(raw);
    setOpenTime(formatted);

    // 🔥 여기서 자동 포커스 이동
    if (/^\d{2}:\d{2}$/.test(formatted) && !movedRef.current) {
      movedRef.current = true;
      setTimeout(() => {
        closeRef.current?.focus();
      }, 0);
    }

    // 다시 입력하면 재허용
    if (formatted.length < 5) {
      movedRef.current = false;
    }
  };

  const isSelectedDay = useCallback(
    (dayOfWeek: number, defaultActive: boolean) => {
      if (!isEdit) return defaultActive;

      return putBusinessProfile?.businessDays?.some(
        (d) => d.dayOfWeek === dayOfWeek && d.active
      );
    },
    [isEdit, putBusinessProfile?.businessDays]
  );

  const normalizeTime = (value: string) => {
    if (!value) return '00:00';

    const numbers = value.replace(/\D/g, '').padEnd(4, '0');

    let hour = Number(numbers.slice(0, 2));
    let minute = Number(numbers.slice(2, 4));

    if (hour >= 24) hour = 0;
    if (minute >= 60) minute = 0;

    return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
  };

  const onBlurTime =
    (value: string, setValue: (v: string) => void, type: 'open' | 'close') =>
    () => {
      if (!putBusinessProfile) return;

      // const normalized = normalizeTime(value);
      const isEmpty = !value || value.replace(/\D/g, '') === '';

      const normalized = isEmpty
        ? type === 'open'
          ? DEFAULT_OPEN_TIME
          : DEFAULT_CLOSE_TIME
        : normalizeTime(value);

      setValue(normalized);

      dispatch(
        businessProfile.putBusinessProfile({
          businessTime: {
            openTime:
              type === 'open'
                ? `${normalized}:00`
                : putBusinessProfile.businessTime.openTime,
            closeTime:
              type === 'close'
                ? `${normalized}:00`
                : putBusinessProfile.businessTime.closeTime,
          },
        })
      );
    };

  const formatTimeWithAutoColon = (value: string) => {
    // 숫자만 추출
    const numbers = value.replace(/\D/g, '');

    // 최대 4자리까지만 허용 (HHmm)
    if (numbers.length > 4) return value.slice(0, 5);

    if (numbers.length <= 2) {
      // H / HH
      return numbers;
    }

    // H H : m m
    const hour = numbers.slice(0, 2);
    const minute = numbers.slice(2);

    return minute ? `${hour}:${minute}` : `${hour}:`;
  };

  const onChangeTime =
    (setValue: (v: string) => void) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const raw = e.target.value;

      // 숫자와 : 만 허용
      if (!/^[0-9:]*$/.test(raw)) return;

      const formatted = formatTimeWithAutoColon(raw);
      setValue(formatted);
    };

  useEffect(() => {
    if (!isEdit) {
      setOpenTime(profile.businessTime.openTime.slice(0, 5));
      setCloseTime(profile.businessTime.closeTime.slice(0, 5));
      movedRef.current = false;
    }
  }, [isEdit, profile.businessTime.openTime, profile.businessTime.closeTime]);

  return (
    <DateSection>
      <DateWrap>
        {businessDaysWithTitle.map((day) => (
          <DateButton
            key={day.title}
            data-selected={isSelectedDay(day.dayOfWeek, day.active)}
            data-edit={isEdit}
            onClick={onClickDays(day)}
          >
            {day.title}
          </DateButton>
        ))}
      </DateWrap>
      <TimeWrap>
        {!isEdit ? (
          <>
            <TimeSelect>{profile.businessTime.openTime.slice(0, 5)}</TimeSelect>
            -
            <TimeSelect>
              {profile.businessTime.closeTime.slice(0, 5)}
            </TimeSelect>
          </>
        ) : (
          <>
            <TimeInput
              ref={openRef}
              value={openTime}
              onChange={onChangeOpenTime}
              onBlur={onBlurTime(openTime, setOpenTime, 'open')}
              maxLength={5}
              placeholder="00:00"
              inputMode="numeric"
            />
            <EditHypen>-</EditHypen>
            <TimeInput
              ref={closeRef}
              value={closeTime}
              onChange={onChangeTime(setCloseTime)}
              onBlur={onBlurTime(closeTime, setCloseTime, 'close')}
              maxLength={5}
              placeholder="23:59"
              inputMode="numeric"
            />
          </>
        )}

        <Country data-edit={isEdit}>
          {getCountryNameByCode({
            contactCountryCode: profile.name.countryCode,
            countryCode: profile.countryCode,
          })}
        </Country>
        <TimeDesc>
          <MultiLineText
            translationKey={t('pleaseSetYourReqularBusinessHours')}
          />
        </TimeDesc>
      </TimeWrap>
    </DateSection>
  );
};

export default BusinessTimeSettings;

const DateSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: start;
  gap: 17px;
`;

const DateWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
`;

const DateButton = styled.button`
  font-size: 15px;
  line-height: 140%;
  color: #000000;
  border-radius: 10px;
  height: 53px;
  width: 78px;
  border: 1px solid #ababab;
  background: #ffffff;
  cursor: auto;
  &[data-selected='true'] {
    background: #5b4be8;
    color: #ffffff;
  }
  &[data-edit='true'] {
    cursor: pointer;
  }
`;

const TimeWrap = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 46px;
`;

const TimeSelect = styled.div`
  padding: 8.5px 15px;
  box-sizing: border-box;
  width: 200px;
  height: 60px;
  border-radius: 8px;
  border: 1px solid #dcdde7;
  text-align: start;
  font-size: 20px;
  line-height: normal;
  color: #000000;
  display: flex;
  align-items: center;
`;

const EditHypen = styled.div`
  color: #b974ff;
`;

const Country = styled(TimeSelect)`
  font-family: ${fonts.family.pretendard};
  font-weight: ${fonts.weight.regular};
  font-size: 20px;
  line-height: 140%;
  display: flex;
  align-items: center;
  &[data-edit='true'] {
    border: 1px solid #b974ff;
  }
`;

const TimeInput = styled.input`
  padding: 8.5px 15px;
  box-sizing: border-box;
  width: 200px;
  height: 60px;
  border-radius: 8px;
  border: 1px solid #b974ff;
  color: #c285ff;
  font-family: ${fonts.family.pretendard};
  font-size: 20px;
  line-height: normal;
  text-align: start;
  outline: none;

  &::placeholder {
    color: #aaa;
  }
`;

const TimeDesc = styled.div`
  font-size: 12px;
  letter-spacing: -3%;
  color: #312e4b;
  text-align: start;
`;
